import _ from 'lodash';
import { useEffect, useRef } from 'react';
import type { TBulkAction } from '@/shared/model';
import { BULK_ACTION_ENUM } from '@/shared/model';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectBulkAction,
  selectBulkActionSwap,
  selectControlModal,
  useReportStore,
} from '@/entities/report';
import getBulkActionMenu from './handler';
import type { BulkActionProps } from './index';

const BULK_ACTION_MAP = {
  [BULK_ACTION_ENUM.NOTHING]: 'Page',
  [BULK_ACTION_ENUM.ALL_N]: 'Change all to N beat',
  [BULK_ACTION_ENUM.ALL_S]: 'Change all to S beat',
  [BULK_ACTION_ENUM.ALL_V]: 'Change all to V beat',
  [BULK_ACTION_ENUM.ALL_Q]: 'Change all to Q beat',
  [BULK_ACTION_ENUM.ALL_DELETE]: 'Delete all beats',
  [BULK_ACTION_ENUM.MARK_REVIEWED]: 'Mark as reviewed',
};

/**
 * Backs the beats bulk-action menu, arming an action for the next drag selection. Builds the menu once per template mode, mirrors the armed action into the report store, opens the swap-all-by-page template modal instead of arming when the swap entry is picked, and registers a keydown listener that arms actions from the number keys while the tab is hovered.
 *
 * @param props - Disabled flag, report-store tab key, DOM id of the hover zone and whether the grid is in template mode.
 * @returns The disabled flag, the menu entry ref, the armed bulk action, the menu click handler and the confirmation-text builder.
 */
const useBulkAction = ({ isDisabled, keyRecoil, tabId, isTemplate }: BulkActionProps) => {
  const bulkActionMenu = useRef(getBulkActionMenu(isTemplate));

  const bulkAction = useReportStore(selectBulkAction(keyRecoil));
  const setBulkActionAction = useReportStore((s) => s.actions.setBulkAction);
  /**
   * Arms the bulk action for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes this tab's modal visibility flags. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads this tab's modal visibility flags from the report store. Applies the control-modal selector to a snapshot instead of subscribing, so click handlers see the current flags.
   *
   * @returns The tab's control modal flags.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const bulkActionSwap = useReportStore(selectBulkActionSwap(keyRecoil));
  const setBulkActionSwapAction = useReportStore((s) => s.actions.setBulkActionSwap);
  /**
   * Writes the template selected as the bulk swap target. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBulkActionSwap = (v: Parameters<typeof setBulkActionSwapAction>[1]) =>
    setBulkActionSwapAction(keyRecoil, v);

  // IControlModal (Recoil/model.ts) doesn't declare the swap-template fields these
  // components read/write; they exist at runtime but not in the shared type.
  /**
   * Opens the swap-all-templates-by-page modal from the bulk-action menu. Resets the modal flags first and reopens after a short delay when another swap modal is already showing, so the dialog remounts, otherwise sets the flag directly.
   *
   * @returns Nothing.
   */
  const handleOpenSwapModal = () => {
    const controlModal = getControlModal() as any;
    if (controlModal.isShowSwapTemplate || controlModal.isShowSwapAllTemplateById) {
      setControlModal(DEFAULT_CONTROL_MODAL_VALUE);
      setTimeout(() => {
        setControlModal({
          ...DEFAULT_CONTROL_MODAL_VALUE,
          isShowSwapAllTemplateByPage: true,
        } as any);
      }, 100);
    } else {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowSwapAllTemplateByPage: true } as any);
    }
  };

  /**
   * Handles a pick from the bulk-action menu. Diverts the swap-template entry to the swap modal and otherwise arms the picked action in the report store.
   *
   * @param item - The clicked menu entry, whose props carry the bulk action value.
   * @returns Nothing.
   */
  const onClickItem = ({ item }: { item: any }) => {
    if (item.props.value === BULK_ACTION_ENUM.SWAP_TEMPLATE) {
      handleOpenSwapModal();
      return;
    }
    setBulkAction(item.props.value);
  };

  /**
   * Produces the confirmation wording for the bulk action currently armed. Names the target template when a template swap is armed in template mode, otherwise looks the phrasing up in the bulk-action label map.
   *
   * @param bulkAction - The armed bulk action to describe.
   * @returns The human-readable sentence for the armed action.
   */
  const getBulkActionText = (bulkAction: TBulkAction) => {
    if (bulkAction === BULK_ACTION_ENUM.SWAP_TEMPLATE && isTemplate) {
      return `Swap the template to ${bulkActionSwap?.name}`;
    }
    return (BULK_ACTION_MAP as any)[bulkAction];
  };

  useEffect(() => {
    /**
     * Arms a bulk beat action from the number keys. Ignores repeats, modifier-held presses and presses made outside the hovered tab zone, maps keys 1 to 6 onto the do-nothing, N, S, V, Q and delete actions, clears the swap target when do-nothing is picked in template mode, opens the swap modal on the next key in template mode and arms mark-as-reviewed on the last.
     *
     * @param event - The keyboard event being handled.
     * @returns Nothing.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(tabId);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 49: {
          // 1
          setBulkAction(BULK_ACTION_ENUM.NOTHING);
          if (isTemplate) {
            setBulkActionSwap((prev: any) => {
              if (_.isEmpty(prev)) return prev;
              return [];
            });
          }
          break;
        }
        case 50: {
          // 2
          setBulkAction(BULK_ACTION_ENUM.ALL_N);
          break;
        }
        case 51: {
          // 3
          setBulkAction(BULK_ACTION_ENUM.ALL_S);
          break;
        }
        case 52: {
          // 4
          setBulkAction(BULK_ACTION_ENUM.ALL_V);
          break;
        }
        case 53: {
          // 5
          setBulkAction(BULK_ACTION_ENUM.ALL_Q);
          break;
        }
        case 54: {
          // 6
          setBulkAction(BULK_ACTION_ENUM.ALL_DELETE);
          break;
        }
        case 55: {
          // 8
          if (isTemplate) {
            handleOpenSwapModal();
          }
          break;
        }
        case 56: {
          // 7
          setBulkAction(BULK_ACTION_ENUM.MARK_REVIEWED);
          break;
        }
        default: {
          break;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tabId, isTemplate]);

  return {
    isDisabled,
    bulkActionMenu,
    bulkAction,
    onClickItem,
    getBulkActionText,
  };
};

export default useBulkAction;
